import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import { toSnakeCaseKeys } from '@/shared/utils/case.utils'
import { persistor } from '@/store'

// Token refresh queue to prevent race conditions
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

function onRefreshFailed() {
  refreshSubscribers = []
}

// Prefer environment variable if set. Otherwise:
// - In development, default to Vite proxy path '/api/v1' to avoid CORS.
// - In production (or builds without proxy), fall back to localhost:8080.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? '/api/v1' : 'http://localhost:8080/api/v1')

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const isFormData =
      typeof FormData !== 'undefined' &&
      config.data &&
      config.data instanceof FormData
    // Thân nhị phân (âm thanh PCM của Thử nghiệm Thư ký y khoa): đổi khoá sẽ biến nó thành {} hoặc object chỉ số.
    const isBinary =
      config.data instanceof ArrayBuffer ||
      ArrayBuffer.isView(config.data) ||
      (typeof Blob !== 'undefined' && config.data instanceof Blob)

    if (!isFormData && !isBinary) {
      if (config.data) {
        config.data = toSnakeCaseKeys(config.data)
      }
      if (config.params) {
        config.params = toSnakeCaseKeys(config.params)
      }
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors globally and unwrap data
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response for debugging (remove in production)
    // Không bao giờ in giá trị PII đầy đủ (POST /admin/pii/reveal) ra console.
    const isDev =
      import.meta.env.DEV && !response.config.url?.includes('/admin/pii/')
    if (isDev) {
      console.group(
        `API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`
      )
      console.log('Original Response:', response.data)
    }

    // Check for JWT_EXPIRED error_code in successful response
    const data = response.data
    if (data && data.error_code === 'JWT_EXPIRED') {
      if (isDev) {
        console.groupEnd()
      }
      // Treat this as an error and trigger token refresh
      const error = new Error('JWT_EXPIRED') as any
      error.response = response
      error.config = response.config
      return Promise.reject(error)
    }

    if (isDev) {
      console.groupEnd()
    }

    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Do not attempt token refresh for auth endpoints whose own 401 means
    // something other than "access token expired" — a wrong password, an
    // expired/consumed mfa_token, or (for the self-service TOTP reconfigure
    // start step) a wrong current password. Treating those as token-expiry
    // would silently burn the caller's real refresh token and redirect to
    // /login instead of surfacing the actual error.
    const requestUrl = (originalRequest && originalRequest.url) || ''
    const isAuthBypass =
      requestUrl.includes('/admin/auth/login-with-password') ||
      requestUrl.includes('/admin/auth/verify-totp') ||
      requestUrl.includes('/admin/auth/refresh-token') ||
      requestUrl.includes('/admin/auth/logout') ||
      requestUrl.includes('/admin/me/totp/reconfigure')

    // Check for JWT_EXPIRED in response data or 401 status
    const errorCode = (error.response?.data as any)?.error_code
    const isTokenExpired = errorCode === 'JWT_EXPIRED' || error.response?.status === 401

    if (isTokenExpired && !originalRequest._retry && !isAuthBypass) {
      originalRequest._retry = true

      if (isRefreshing) {
        // Another request is already refreshing, queue this one
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
            }
            resolve(axiosInstance(originalRequest))
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Attempt to refresh token
        const response = await axios.post(`${API_BASE_URL}/admin/auth/refresh-token`, {
          refresh_token: refreshToken,
        })

        const responseData = response.data

        // Check if refresh was successful (status can be "success" string or 200 number)
        if ((responseData.status === 'success' || responseData.status === 200) && !responseData.error_code && responseData.data) {
          const newAccessToken = responseData.data.access_token
          localStorage.setItem('accessToken', newAccessToken)

          // Persist new refresh token if backend rotates them
          if (responseData.data.refresh_token) {
            localStorage.setItem('refreshToken', responseData.data.refresh_token)
          }

          // Notify all queued requests
          onTokenRefreshed(newAccessToken)
          isRefreshing = false

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          }
          return axiosInstance(originalRequest)
        } else {
          throw new Error('Token refresh failed')
        }
      } catch (refreshError) {
        isRefreshing = false
        onRefreshFailed()
        // Refresh failed - clear tokens and redirect to login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        // Purge persisted state
        await persistor.purge()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle 403 - Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data)
    }

    // Handle 500 - Server error
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

export default axiosInstance
